import { COLOR_CODE } from "theme/components/Variants/constants";
import ColorCircle from "theme/components/Variants/ColorCircle";
import Image from "core-web/components/Image";
import { NO_STOCK_NO_PRICE_PRODUCT } from "theme/config/constants";
import Price from "theme/components/products/Price";
import PropTypes from "prop-types";
import React from "react";
import TextStyles from "theme/components/text/TextStyles";
import colors from "theme/config/colors";
import comparePriceToParent from "core-web/utils/comparePriceToParent";
import discountInPercentage from "core-web/utils/discountInPercentage";
import { getFlag } from "core-web/utils/getFlag";
import { getPriceColor } from "core-web/utils/getPriceColor";
import getPricelistCodePrefix from "core-web/utils/getPricelistCodePrefix";
import hexWithOpacity from "core-web/utils/hexWithOpacity";
import { keyframes } from "react-emotion";
import styled from "theme/libs/styled";
import transformColor from "theme/libs/wordpress/utils/transformColor";
import { useSelector } from "react-redux";
import { useTheme } from "emotion-theming";

const DURATION = 450;

const animateBackground = keyframes`
    0% {
        background-color: inherit;
        opacity: inherit;

    }
    100% {
        background-color: ${colors.lightGreen};
        opacity: 1;
    }

`;

const StyledSpan = styled("span")`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const InnerWrapper = styled("div")`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin: 8px 0 0 0;

    &:not(.color) {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(164px, 1fr));
    }

    &[data-overlay="true"] {
        margin: 24px 0;

        &:not(.color) {
            ${({ theme }) => theme.below.tabletLg} {
                grid-template-columns: 1fr 1fr;
            }
        }
    }
`;

const VariantWrapper = styled("div", { shouldForwardProp: (prop) => ["selectedVariant"].indexOf(prop) === -1 })``;

const VariantBlock = styled("div")`
    border-radius: 8px;
    display: flex;

    justify-content: center;
    align-items: center;
    cursor: pointer;
    user-select: none;
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid rgba(0, 0, 0, 0.3);

    &:not(.color) {
        width: auto;
        min-height: 82px;
        padding: 12px;
        position: relative;

        &[data-active="true"] {
            animation: ${animateBackground} ${DURATION}ms ease forwards;
            border: 1px solid ${({ theme }) => theme.colors.darkGreen};
        }

        &[data-out-of-stock="true"] {
            background: ${({ theme }) =>
                `linear-gradient(to top right, ${hexWithOpacity(
                    theme.colors.white,
                    30,
                )} calc(50% - 1px), ${hexWithOpacity(theme.colors.black, 30)} , ${hexWithOpacity(
                    theme.colors.white,
                    30,
                )} calc(50% + 1px))`};

            &[data-active="true"] {
                background: ${({ theme }) =>
                    `linear-gradient(to top right, ${hexWithOpacity(
                        theme.colors.lightGreen,
                        30,
                    )} calc(50% - 1px), ${hexWithOpacity(theme.colors.black, 30)} , ${hexWithOpacity(
                        theme.colors.lightGreen,
                        30,
                    )} calc(50% + 1px))`};
            }
        }
    }

    &.color {
        background-color: ${({ theme }) => theme.colors.darkGreige};
        width: 78px;
        height: 78px;
        overflow: hidden;

        & > div {
            display: flex;
            align-items: center;
            justify-content: center;

            img {
                border-radius: 8px;
                height: 100%;
                width: 100%;
            }
        }

        ${({ theme }) => theme.above.mobileLg} {
            width: 64px;
            height: 64px;
        }

        &[data-active="true"] {
            animation: ${animateBackground} ${DURATION}ms ease forwards;
            border: 1px solid ${({ theme }) => theme.colors.darkGreen};
        }

        &[data-out-of-stock="true"] {
            background: ${({ theme }) =>
                `linear-gradient(to top right, ${theme.colors.darkGreige} calc(50% - 1px), ${theme.colors.black} , ${theme.colors.darkGreige} calc(50% + 1px))`};

            &[data-active="true"] {
                background: ${({ theme }) =>
                    `linear-gradient(to top right, ${theme.colors.lightGreen} calc(50% - 1px), ${theme.colors.black} , ${theme.colors.lightGreen} calc(50% + 1px))`};
            }
        }
    }

    &[data-out-of-stock="true"] {
        > * {
            opacity: 0.3;
        }
    }

    ${({ theme }) => theme.media.hover} {
        &:hover {
            border-color: ${({ theme }) => theme.colors.darkGreen};
            &[data-active="true"] {
                border-color: ${({ theme }) => hexWithOpacity(theme.colors.darkGreen, 50)};
            }
        }
    }
`;

const VariantBlocks = ({
    variant,
    index,
    t,
    priceFormatProps,
    checkPossibleSelection,
    checkStockStatus,
    selectedParametrics,
    selectParametricValue,
    getVariantByParametricSelection,
    currentSelectedParametrics,
    source,
    ...props
}) => {
    const defaultPricelistId = useSelector(({ application }) => application?.defaultPriceList?.id);
    const { price_colors: priceColorFlags } =
        useSelector(({ application }) => application.config.options.products) || {};
    const { darkGreige, black, red, white, lightGreige, darkGreen } = useTheme().colors;
    const { code, name, id, selectedValue } = variant;
    const isOverlay = source === "overlay";

    return (
        <VariantWrapper key={index} {...props}>
            <TextStyles
                as="span"
                textStyles={isOverlay ? "Buenos Aires/32-120 SemiBold" : "Buenos Aires/14-120 SemiBold"}
            >
                {`${t(selectedValue ? "pdp.chosen" : "pdp.choose", { variant: name })}${
                    (selectedValue && `: ${selectedValue}`) || ""
                }`}
            </TextStyles>
            <InnerWrapper className={code}>
                {variant?.values.map((item, i) => {
                    const { value, displayedValue } = item;
                    const isAvailable = checkPossibleSelection(index, id, value);
                    const stockStatus = checkStockStatus(index, id, value);
                    const thisVariant = getVariantByParametricSelection(
                        currentSelectedParametrics(selectedParametrics, id, value),
                    );

                    const {
                        price: priceExVat,
                        price_list_code: priceListCode,
                        price_list_id: priceListId,
                        price_standard: standardPriceExVat,
                        vat_rate: vatRate,
                        format,
                        included_products: includedProducts,
                        googleDiscountPrice,
                        specialPrice,
                    } = thisVariant || {};
                    const checkStandardPrice = priceListId ? defaultPricelistId !== priceListId : true;
                    let displayStandardPriceExVat = standardPriceExVat;
                    if (comparePriceToParent(includedProducts)) {
                        displayStandardPriceExVat = comparePriceToParent(includedProducts);
                    } else if (googleDiscountPrice || specialPrice) {
                        displayStandardPriceExVat =
                            !standardPriceExVat || priceExVat > standardPriceExVat ? priceExVat : standardPriceExVat;
                    }

                    const discount =
                        checkStandardPrice &&
                        discountInPercentage(
                            displayStandardPriceExVat,
                            specialPrice || googleDiscountPrice || priceExVat,
                        );

                    const hidePrice = !!getFlag(NO_STOCK_NO_PRICE_PRODUCT, format?.flags)?.code;

                    if (!isAvailable) {
                        return null;
                    }

                    const hasImage = !!item?.image;
                    const variantColor = item?.hexColor;
                    const isColor = COLOR_CODE === code;
                    let priceColor = transformColor(getPriceColor(priceColorFlags, format?.flags));
                    if (priceColor) {
                        priceColor = colors[priceColor];
                    } else {
                        priceColor = selectedValue === value ? darkGreen : hexWithOpacity(black, 60);
                    }

                    const pricelistPrefix = getPricelistCodePrefix(priceListCode);
                    const isCompetitorPricelist = pricelistPrefix === "competitor";

                    const showDiscount = !!discount && !hidePrice && !isColor && !isCompetitorPricelist;

                    return (
                        <VariantBlock
                            className={code}
                            data-overlay={isOverlay}
                            data-out-of-stock={!stockStatus}
                            data-active={selectedValue === value}
                            key={i}
                            onClick={() => selectParametricValue(index, selectedValue === value ? null : value)}
                        >
                            {hasImage ? (
                                <Image
                                    backgroundColor={darkGreige}
                                    src={{ url: item.image.url, width: 76, height: 76 }}
                                />
                            ) : variantColor ? (
                                <ColorCircle height="20px" width="20px" background={variantColor} />
                            ) : (
                                <StyledSpan>
                                    {showDiscount && (
                                        <TextStyles
                                            as="span"
                                            color={red}
                                            backgroundColor={selectedValue === value ? white : lightGreige}
                                            p="2px 8px"
                                            borderRadius="8px"
                                            mb="6px"
                                            textStyles="Buenos Aires/12-120 Regular"
                                        >
                                            {discount}%
                                        </TextStyles>
                                    )}
                                    <TextStyles
                                        as="span"
                                        textStyles="Buenos Aires/14-120 Regular"
                                        color={selectedValue === value ? darkGreen : black}
                                    >
                                        {displayedValue || value}
                                    </TextStyles>
                                    {!!priceExVat && !hidePrice && !isColor && (
                                        <Price
                                            {...priceFormatProps}
                                            as="span"
                                            standardPriceColor={priceColor}
                                            textAlign="center"
                                            color={priceColor}
                                            priceExVat={specialPrice || googleDiscountPrice || priceExVat}
                                            standardPriceExVat={displayStandardPriceExVat}
                                            vatRate={vatRate}
                                            priceListId={priceListId}
                                            skipDiscountCheck={!!specialPrice || !!googleDiscountPrice}
                                            isCompetitorPricelist={isCompetitorPricelist}
                                        />
                                    )}
                                </StyledSpan>
                            )}
                        </VariantBlock>
                    );
                })}
            </InnerWrapper>
        </VariantWrapper>
    );
};

VariantBlocks.propTypes = {
    checkPossibleSelection: PropTypes.func.isRequired,
    checkStockStatus: PropTypes.func.isRequired,
    currentSelectedParametrics: PropTypes.func.isRequired,
    getVariantByParametricSelection: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    priceFormatProps: PropTypes.object.isRequired,
    selectParametricValue: PropTypes.func.isRequired,
    selectedParametrics: PropTypes.array.isRequired,
    source: PropTypes.string,
    t: PropTypes.func.isRequired,
    variant: PropTypes.object.isRequired,
};

VariantBlocks.defaultProps = {
    source: "",
};

export default VariantBlocks;
